/**
 * F21 (Wave 2D) — admin payment-config override apply / clear page.
 *
 * Route: `/venues/:venueId/payment-config/override`
 * Permission: `payment_config:write` (super_admin + content_moderator) —
 *   gated by `<ProtectedRoute permission="payment_config:write">` in
 *   `App.tsx`. Finance role can READ the sibling /payment-config index
 *   but never lands here.
 *
 * Two modes:
 *   - When no override is active → "Apply override" form (full method +
 *     billing-model picker + 10–500 char reason). Submitting fires the
 *     `paymentConfig.adminOverrideApplied` orchestrator event server-side.
 *   - When an override IS active → "Clear override" form (reason only;
 *     server fires the sibling Cleared event).
 *
 * Both modes use React Hook Form + Zod with the Wave-1 frozen schemas
 * (`ApplyAdminOverrideInputSchema`, `ClearAdminOverrideInputSchema`).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  FormField,
  Radio,
  Skeleton,
  TextInput,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import type {
  ApplyAdminOverrideInput,
  BillingModel,
  ClearAdminOverrideInput,
  PaymentMethod,
  PayoutInstruction,
  VenuePaymentConfig,
} from '@bravemove/types';
import {
  ApplyAdminOverrideInputSchema,
  BILLING_MODELS,
  ClearAdminOverrideInputSchema,
  PAYMENT_METHODS,
} from '@bravemove/types';
import {
  adminPaymentConfigApi,
  type AdminPaymentConfigResponse,
} from '../../../../api/paymentConfig';
import { translateApiError } from '../../../../auth/errors';

function instructionFor(
  method: PaymentMethod,
  existing: VenuePaymentConfig | null,
): PayoutInstruction {
  const found = existing?.payoutInstructions.find((pi) => pi.method === method);
  if (found) return found;
  switch (method) {
    case 'whish':
      return { method: 'whish', label: 'Whish', details: '', whishPhone: '' };
    case 'omt':
      return { method: 'omt', label: 'OMT', details: '', omtBeneficiary: '' };
    case 'cash':
      return { method: 'cash', label: 'Cash', details: '' };
    case 'other':
      return { method: 'other', label: 'Other', details: '' };
  }
}

type ApplyFormShape = ApplyAdminOverrideInput;

export function AdminVenuePaymentConfigOverridePage() {
  const { t } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { venueId } = useParams<{ readonly venueId: string }>();

  const [data, setData] = useState<AdminPaymentConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!venueId) return;
    let cancelled = false;
    setLoading(true);
    // BP-08 — load through the `payment_config:write`-gated override-view
    // sibling endpoint: content_moderator (who owns this page) does NOT hold
    // `payments:read`, so the canonical detail GET would 403 and the forms
    // would never render.
    adminPaymentConfigApi
      .overrideView(venueId)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(translateApiError(err, t));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [venueId, t]);

  const overrideActive =
    data?.config.adminOverride !== null && data?.config.adminOverride !== undefined;

  // ─── Apply form ────────────────────────────────────────────────────────────
  const apply = useForm<ApplyFormShape>({
    resolver: zodResolver(ApplyAdminOverrideInputSchema) as unknown as never,
    defaultValues: {
      acceptedMethods: ['cash'],
      payoutInstructions: [],
      billingModel: 'commission_only',
      reason: '',
    },
  });

  const clear = useForm<ClearAdminOverrideInput>({
    resolver: zodResolver(ClearAdminOverrideInputSchema) as unknown as never,
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (!data) return;
    apply.reset({
      acceptedMethods: [...data.config.acceptedMethods],
      payoutInstructions: [...data.config.payoutInstructions],
      billingModel: data.config.billingModel,
      reason: '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (!venueId) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4" data-testid="admin-override-page-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div data-testid="admin-override-page-error">
        <Banner tone="danger" title={loadError ?? 'Failed to load'} />
      </div>
    );
  }

  // ─── Apply override submit ─────────────────────────────────────────────────
  const onApply = apply.handleSubmit(async (values) => {
    if (!venueId) return;
    setSubmitting(true);
    try {
      const updated = await adminPaymentConfigApi.applyOverride(venueId, values);
      // The write response carries `{ config, venueUpdatedAt }` only — merge
      // into the detail state (auditHistory stays as loaded).
      setData((prev) => (prev ? { ...prev, ...updated } : prev));
      toast.push({
        tone: 'success',
        title: t('payment-config:toast.override_applied'),
      });
      navigate(`/venues/${encodeURIComponent(venueId)}/payment-config`);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setSubmitting(false);
    }
  });

  const onClear = clear.handleSubmit(async (values) => {
    if (!venueId) return;
    setSubmitting(true);
    try {
      const updated = await adminPaymentConfigApi.clearOverride(venueId, values);
      // Same merge as onApply — the write response has no auditHistory.
      setData((prev) => (prev ? { ...prev, ...updated } : prev));
      toast.push({
        tone: 'success',
        title: t('payment-config:toast.override_cleared'),
      });
      navigate(`/venues/${encodeURIComponent(venueId)}/payment-config`);
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setSubmitting(false);
    }
  });

  // ─── Render: Clear form when override active ───────────────────────────────
  if (overrideActive) {
    return (
      <div className="max-w-2xl flex flex-col gap-4" data-testid="admin-override-clear-page">
        <h1 className="text-h1 font-bold text-ink">
          {t('payment-config:admin_override.clear_form.title')}
        </h1>
        <p className="text-body text-ink-2">
          {t('payment-config:admin_override.clear_form.description')}
        </p>
        <Card>
          <form className="flex flex-col gap-4" onSubmit={onClear}>
            <FormField
              label={t('payment-config:admin_override.clear_form.reason_label')}
              htmlFor="clear-reason"
              error={clear.formState.errors.reason?.message}
            >
              <Textarea
                id="clear-reason"
                rows={4}
                placeholder={t('payment-config:admin_override.clear_form.reason_placeholder')}
                {...clear.register('reason')}
                data-testid="admin-override-clear-reason"
              />
            </FormField>
            <div className="flex flex-wrap gap-2 border-t border-hairline pt-3">
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={submitting}
                disabled={submitting}
                data-testid="admin-override-clear-submit"
              >
                {t('payment-config:admin_override.clear_form.submit')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/venues/${encodeURIComponent(venueId)}/payment-config`)}
              >
                {/* BP-10 — dedicated cancel key; `contact_support_cta`'s
                    value is the venue-owner-facing "Contact support" label. */}
                {t('payment-config:admin_override.cancel', {
                  defaultValue: 'Cancel',
                })}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    );
  }

  // ─── Render: Apply form ────────────────────────────────────────────────────
  const acceptedMethods = apply.watch('acceptedMethods') ?? [];
  const payoutInstructions = apply.watch('payoutInstructions') ?? [];
  const billingModel = apply.watch('billingModel') ?? 'commission_only';

  // Normalise an instruction loaded from Zod-inferred storage (which carries
  // `omtBranch?: string | undefined`) onto the strict `PayoutInstruction` union
  // (`omtBranch?: string`). Drop the key entirely when undefined.
  const normaliseInstruction = (pi: {
    readonly method: PaymentMethod;
    readonly label: string;
    readonly details: string;
    readonly whishPhone?: string;
    readonly omtBeneficiary?: string;
    readonly omtBranch?: string | undefined;
  }): PayoutInstruction => {
    if (pi.method === 'whish') {
      return {
        method: 'whish',
        label: pi.label,
        details: pi.details,
        whishPhone: pi.whishPhone ?? '',
      };
    }
    if (pi.method === 'omt') {
      const base = {
        method: 'omt' as const,
        label: pi.label,
        details: pi.details,
        omtBeneficiary: pi.omtBeneficiary ?? '',
      };
      return pi.omtBranch !== undefined && pi.omtBranch.length > 0
        ? { ...base, omtBranch: pi.omtBranch }
        : base;
    }
    return { method: pi.method, label: pi.label, details: pi.details };
  };

  const toggleMethod = (method: PaymentMethod, enabled: boolean) => {
    const set = new Set<PaymentMethod>(acceptedMethods);
    if (enabled) set.add(method);
    else set.delete(method);
    const ordered: PaymentMethod[] = PAYMENT_METHODS.filter((m) => set.has(m));
    apply.setValue('acceptedMethods', ordered, { shouldValidate: true });

    // Sync payoutInstructions so the array stays consistent with the toggled set.
    const map = new Map<PaymentMethod, PayoutInstruction>();
    for (const pi of payoutInstructions) {
      map.set(pi.method, normaliseInstruction(pi));
    }
    if (enabled) {
      if (!map.has(method)) {
        map.set(method, normaliseInstruction(instructionFor(method, data.config)));
      }
    } else {
      map.delete(method);
    }
    const nextInstructions: PayoutInstruction[] = [];
    for (const m of ordered) {
      const pi = map.get(m);
      if (pi) nextInstructions.push(pi);
    }
    apply.setValue('payoutInstructions', nextInstructions, { shouldValidate: true });
  };

  /**
   * Accepts a loose Zod-inferred shape (omtBranch may be `undefined`) and
   * normalises onto the strict union before storing.
   */
  const setInstruction = (next: {
    readonly method: PaymentMethod;
    readonly label: string;
    readonly details: string;
    readonly whishPhone?: string;
    readonly omtBeneficiary?: string;
    readonly omtBranch?: string | undefined;
  }) => {
    const normalised: PayoutInstruction = (() => {
      if (next.method === 'whish') {
        return {
          method: 'whish',
          label: next.label,
          details: next.details,
          whishPhone: next.whishPhone ?? '',
        };
      }
      if (next.method === 'omt') {
        const base = {
          method: 'omt' as const,
          label: next.label,
          details: next.details,
          omtBeneficiary: next.omtBeneficiary ?? '',
        };
        return next.omtBranch !== undefined && next.omtBranch.length > 0
          ? { ...base, omtBranch: next.omtBranch }
          : base;
      }
      return { method: next.method, label: next.label, details: next.details };
    })();
    const map = new Map<PaymentMethod, PayoutInstruction>();
    for (const pi of payoutInstructions) {
      map.set(pi.method, normaliseInstruction(pi));
    }
    map.set(normalised.method, normalised);
    const ordered: PayoutInstruction[] = [];
    for (const m of acceptedMethods) {
      const pi = map.get(m);
      if (pi) ordered.push(pi);
    }
    apply.setValue('payoutInstructions', ordered, { shouldValidate: true });
  };

  return (
    <div className="max-w-3xl flex flex-col gap-4" data-testid="admin-override-apply-page">
      <h1 className="text-h1 font-bold text-ink">
        {t('payment-config:admin_override.apply_form.title')}
      </h1>
      <p className="text-body text-ink-2">
        {t('payment-config:admin_override.apply_form.description')}
      </p>
      <Card>
        <form className="flex flex-col gap-5" onSubmit={onApply}>
          <section className="flex flex-col gap-3">
            <h2 className="text-h2 font-bold text-ink">
              {t('payment-config:methods.section_title')}
            </h2>
            {apply.formState.errors.acceptedMethods ? (
              <Banner
                tone="danger"
                title={t('payment-config:validation.PAYMENT_CONFIG_AT_LEAST_ONE_METHOD')}
              />
            ) : null}
            {PAYMENT_METHODS.map((m) => {
              const enabled = acceptedMethods.includes(m);
              const inst = payoutInstructions.find((pi) => pi.method === m);
              return (
                <div
                  key={m}
                  className="rounded-md border border-hairline-2 bg-surface p-3"
                  data-testid={`admin-override-method-row-${m}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={enabled}
                      onChange={(e) => toggleMethod(m, e.target.checked)}
                      data-testid={`admin-override-method-checkbox-${m}`}
                    />
                    <div className="flex-1">
                      <div className="text-[13.5px] font-semibold text-ink">
                        {t(`payment-config:methods.${m}.label`)}
                      </div>
                      <p className="text-[12px] text-ink-2 mt-0.5">
                        {t(`payment-config:methods.${m}.toggle_hint`)}
                      </p>
                    </div>
                  </div>
                  {enabled && inst ? (
                    <div className="mt-3 ps-7 border-s border-hairline flex flex-col gap-2">
                      <FormField
                        label={t('payment-config:methods.label_field')}
                        htmlFor={`admin-override-${m}-label`}
                      >
                        <TextInput
                          id={`admin-override-${m}-label`}
                          value={inst.label}
                          onChange={(e) =>
                            setInstruction({ ...inst, label: e.target.value.slice(0, 40) })
                          }
                          maxLength={40}
                          data-testid={`admin-override-${m}-label-input`}
                        />
                      </FormField>
                      {inst.method === 'whish' ? (
                        <FormField
                          label={t('payment-config:methods.whish.phone_label')}
                          htmlFor={`admin-override-whish-phone`}
                        >
                          <TextInput
                            id={`admin-override-whish-phone`}
                            value={inst.whishPhone}
                            onChange={(e) =>
                              setInstruction({ ...inst, whishPhone: e.target.value })
                            }
                            placeholder={t('payment-config:methods.whish.phone_placeholder')}
                            inputMode="tel"
                            data-testid={`admin-override-whish-phone-input`}
                          />
                        </FormField>
                      ) : null}
                      {inst.method === 'omt' ? (
                        <>
                          <FormField
                            label={t('payment-config:methods.omt.beneficiary_label')}
                            htmlFor={`admin-override-omt-beneficiary`}
                          >
                            <TextInput
                              id={`admin-override-omt-beneficiary`}
                              value={inst.omtBeneficiary}
                              onChange={(e) =>
                                setInstruction({
                                  ...inst,
                                  omtBeneficiary: e.target.value.slice(0, 80),
                                })
                              }
                              maxLength={80}
                              data-testid={`admin-override-omt-beneficiary-input`}
                            />
                          </FormField>
                        </>
                      ) : null}
                      <FormField
                        label={t('payment-config:methods.details_field')}
                        htmlFor={`admin-override-${m}-details`}
                      >
                        <Textarea
                          id={`admin-override-${m}-details`}
                          value={inst.details}
                          onChange={(e) =>
                            setInstruction({
                              ...inst,
                              details: e.target.value.slice(0, 200),
                            })
                          }
                          maxLength={200}
                          rows={2}
                          data-testid={`admin-override-${m}-details-input`}
                        />
                      </FormField>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-h2 font-bold text-ink">
              {t('payment-config:billing.section_title')}
            </h2>
            {BILLING_MODELS.map((bm) => (
              <label
                key={bm}
                className="flex items-start gap-3 rounded-md border border-hairline-2 bg-surface p-3 cursor-pointer"
              >
                <Radio
                  name="billingModel"
                  value={bm}
                  checked={billingModel === bm}
                  onChange={() =>
                    apply.setValue('billingModel', bm as BillingModel, { shouldValidate: true })
                  }
                  data-testid={`admin-override-billing-${bm}`}
                />
                <div>
                  <div className="text-[13.5px] font-semibold text-ink">
                    {t(`payment-config:billing.${bm}.title`)}
                  </div>
                  <p className="text-[12.5px] text-ink-2 mt-0.5">
                    {t(`payment-config:billing.${bm}.description`)}
                  </p>
                </div>
              </label>
            ))}
          </section>

          <section>
            <FormField
              label={t('payment-config:admin_override.apply_form.reason_label')}
              htmlFor="apply-reason"
              error={apply.formState.errors.reason?.message}
            >
              <Textarea
                id="apply-reason"
                rows={4}
                placeholder={t('payment-config:admin_override.apply_form.reason_placeholder')}
                {...apply.register('reason')}
                data-testid="admin-override-apply-reason"
              />
            </FormField>
          </section>

          <div className="flex flex-wrap gap-2 border-t border-hairline pt-3">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={submitting}
              disabled={submitting}
              data-testid="admin-override-apply-submit"
            >
              {t('payment-config:admin_override.apply_form.submit')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/venues/${encodeURIComponent(venueId)}/payment-config`)}
            >
              {/* BP-10 — dedicated cancel key; `contact_support_cta`'s
                  value is the venue-owner-facing "Contact support" label. */}
              {t('payment-config:admin_override.cancel', {
                defaultValue: 'Cancel',
              })}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default AdminVenuePaymentConfigOverridePage;
